﻿import axios from 'axios'
import { apiClient } from '@/lib/api-client'
import { accountsPaths } from '@/lib/api-paths'
import { cleanApiParams } from '@/lib/clean-api-params'
import { toUsersQuery } from '../utils/usersQuery'
import type {
  AssignSchoolRequest,
  ChangePasswordRequest,
  CreateInvitationRequest,
  EmailChangeLookup,
  ListInvitationsFilters,
  ListLoginHistoryFilters,
  ListUsersFilters,
  UsersPage,
  LoginRequest,
  LogoutRequest,
  ParentEmailChange,
  ParentEmailChangeList,
  ParentEmailVerificationRequest,
  ParentInviteAcceptRequest,
  ParentInviteLookup,
  ParentRegistrationRequest,
  ParentRegistrationResponse,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  RefreshTokenRequest,
  UpdateProfileRequest,
  RoleChangeRequest,
  RoleAssignmentRequest,
} from '../types/api'
import type {
  AuthTokens,
  Invitation,
  InvitationPreview,
  LoginHistory,
  User,
  UserDetails,
  UserProfile,
} from '../types/models'

// Auth
export const accountsApi = {
  async login(data: LoginRequest): Promise<AuthTokens> {
    const res = await apiClient.post(accountsPaths.login, data)
    return res.data
  },

  async registerParent(data: ParentRegistrationRequest): Promise<ParentRegistrationResponse> {
    const res = await apiClient.post(accountsPaths.registerParent, data)
    return res.data
  },

  async resendParentVerification(email: string): Promise<{ success: boolean; message: string }> {
    const res = await apiClient.post(accountsPaths.resendParentVerification, { email })
    return res.data
  },

  async verifyParentEmail(data: ParentEmailVerificationRequest): Promise<AuthTokens> {
    const res = await apiClient.post(accountsPaths.verifyParent, data)
    return res.data
  },

  async refreshToken(data: RefreshTokenRequest): Promise<AuthTokens> {
    const res = await apiClient.post(accountsPaths.refreshToken, data)
    return res.data.data ?? res.data
  },

  async logout(data: LogoutRequest): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.logout, data)
    return res.data
  },
}

// Profile
export const profileApi = {
  async getProfile(): Promise<UserDetails> {
    const res = await apiClient.get(accountsPaths.profile)
    return res.data.data ?? res.data
  },

  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    const res = await apiClient.put(accountsPaths.profile, data, {
      headers: { 'Content-Type': 'application/json' },
    })
    return res.data.data ?? res.data
  },

  async changePassword(data: ChangePasswordRequest): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.passwordChange, data)
    return res.data
  },
}

// Password reset
export const passwordApi = {
  async requestReset(data: PasswordResetRequest): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.passwordResetRequest, data)
    return res.data
  },
  async confirmReset(
    data: PasswordResetConfirmRequest,
  ): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.passwordResetConfirm, data)
    return res.data
  },
}

// Admin Users

export const adminUsersApi = {
  async listUsersPage(filters: ListUsersFilters = {}): Promise<UsersPage> {
    const res = await apiClient.get(accountsPaths.adminUsers, {
      params: cleanApiParams(toUsersQuery(filters)),
    })
    const body = res.data
    const users: User[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : []

    return {
      users,
      total: typeof body?.total === 'number' ? body.total : users.length,
      limit: typeof body?.limit === 'number' ? body.limit : users.length,
      offset: typeof body?.offset === 'number' ? body.offset : 0,
    }
  },

  /** First page of matches only; use `listUsersPage` when the total or paging matters. */
  async listUsers(filters: ListUsersFilters = {}): Promise<User[]> {
    return (await adminUsersApi.listUsersPage(filters)).users
  },

  async getUser(userId: number): Promise<UserDetails> {
    const res = await apiClient.get(accountsPaths.adminUser(userId))

    return res.data.data ?? res.data
  },

  async updateUser(userId: number, data: Record<string, unknown>): Promise<User> {
    const res = await apiClient.put(accountsPaths.adminUser(userId), data)

    return res.data.data ?? res.data
  },

  async disableUser(userId: number): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserDisable(userId))

    return res.data
  },

  async enableUser(userId: number): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserEnable(userId))

    return res.data
  },

  async deleteUser(userId: number): Promise<{ success: true; message: string }> {
    const res = await apiClient.delete(accountsPaths.adminUser(userId))

    return res.data
  },

  async assignSchool(
    userId: number,
    data: AssignSchoolRequest,
  ): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserAssignSchool(userId), data)

    return res.data
  },

  async removeSchool(
    userId: number,
    schoolId: string,
  ): Promise<{ success: true; message: string }> {
    const res = await apiClient.delete(accountsPaths.adminUserRemoveSchool(userId, schoolId))

    return res.data
  },

  async changeRole(
    userId: number,
    data: RoleChangeRequest,
  ): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserChangeRole(userId), data)

    return res.data
  },

  async assignRole(
    userId: number,
    data: RoleAssignmentRequest,
  ): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserAssignRole(userId), data)

    return res.data
  },

  async removeRole(userId: number, role: string): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.adminUserRemoveRole(userId), {
      role,
    })

    return res.data
  },
}

// Invitations
export const invitationsApi = {
  async createInvitation(data: CreateInvitationRequest): Promise<Invitation> {
    const res = await apiClient.post(accountsPaths.invitations, data)
    return res.data.data ?? res.data
  },
  async listInvitations(filters: ListInvitationsFilters = {}): Promise<Invitation[]> {
    const res = await apiClient.get(accountsPaths.invitations, {
      params: cleanApiParams(filters),
    })
    return res.data.data ?? res.data
  },
  async resendInvitation(inviteId: number): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.invitationResend(inviteId))
    return res.data
  },
  async revokeInvitation(inviteId: number): Promise<{ success: true; message: string }> {
    const res = await apiClient.post(accountsPaths.invitationRevoke(inviteId))
    return res.data
  },
  /** Public: bypasses apiClient so a stale session never triggers refresh or logout. */
  async previewInvitation(token: string): Promise<InvitationPreview> {
    const res = await axios.get(accountsPaths.invitationPreview, {
      baseURL: apiClient.defaults.baseURL,
      params: { token },
    })
    return res.data.data ?? res.data
  },
}

// Login history
export const loginHistoryApi = {
  async list(filters: ListLoginHistoryFilters = {}): Promise<LoginHistory[]> {
    const res = await apiClient.get(accountsPaths.loginHistory, {
      params: cleanApiParams(filters),
    })
    return res.data.data ?? res.data
  },
}

// Admin re-invite of a parent at a new sign-in email

export const parentEmailChangeApi = {
  async list(parentId: number): Promise<ParentEmailChangeList> {
    const res = await apiClient.get(accountsPaths.adminParentEmailChange(parentId))
    return res.data
  },

  async request(
    parentId: number,
    data: { new_email: string; reason: string },
  ): Promise<ParentEmailChange> {
    const res = await apiClient.post(accountsPaths.adminParentEmailChange(parentId), data)
    return res.data
  },

  async resend(parentId: number): Promise<ParentEmailChange> {
    const res = await apiClient.post(accountsPaths.adminParentEmailChangeResend(parentId))
    return res.data
  },

  async cancel(parentId: number): Promise<ParentEmailChange> {
    const res = await apiClient.post(accountsPaths.adminParentEmailChangeCancel(parentId))
    return res.data
  },

  async lookup(token: string): Promise<EmailChangeLookup> {
    const res = await apiClient.post(accountsPaths.emailChangeLookup, { token })
    return res.data
  },

  async confirm(data: {
    token: string
    new_password: string
    confirm_password: string
  }): Promise<{ success: true; message: string; email: string }> {
    const res = await apiClient.post(accountsPaths.emailChangeConfirm, data)
    return res.data
  },
}

/** Imported family's parent opens their invite link (public, no auth). */
export const parentInviteApi = {
  async lookup(token: string): Promise<ParentInviteLookup> {
    const res = await apiClient.post(accountsPaths.parentInviteLookup, { token })
    return res.data
  },

  async accept(data: ParentInviteAcceptRequest): Promise<AuthTokens> {
    const res = await apiClient.post(accountsPaths.parentInviteAccept, data)
    return res.data
  },
}
